import { trackAnalytics } from "src/pages/helpers";
import { DOCUMENT_EDIT_TYPES, EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";

/**
 * Fires "Document Downloaded" (INH-5930) once a document file has actually been
 * handed to the browser. Called from the download thunks, not the click handlers,
 * so every surface (document panel, download dropdown, document library, legacy
 * draft download) reports through one place and failed downloads are not counted.
 *
 * Fields the frontend can't know are sent as null, not guessed. Never throws:
 * the callers are download thunks, and an analytics failure must not turn a
 * completed download into a rejected one.
 */
export const trackDocumentDownloaded = ({
  format,
  documentVersion = null,
  hasTrackedChanges = null,
  basedOnUpload = null,
  threadId = null,
  documentId = null,
  metadata = null,
}) => {
  try {
    trackAnalytics(
      EVENT_NAMES.DOCUMENT_DOWNLOADED,
      {
        [EVENT_PARAMETERS.FORMAT]: format ?? null,
        [EVENT_PARAMETERS.HAS_TRACKED_CHANGES]: hasTrackedChanges,
        [EVENT_PARAMETERS.BASED_ON_UPLOAD]: basedOnUpload,
        [EVENT_PARAMETERS.DOCUMENT_VERSION]: documentVersion != null ? Number(documentVersion) : null,
        [EVENT_PARAMETERS.PLAN_TIER]: metadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.THREAD_ID]: threadId,
        [EVENT_PARAMETERS.DOCUMENT_ID]: documentId,
        [EVENT_PARAMETERS.USER_ID]: metadata?.id ?? null,
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      },
      metadata,
    );
  } catch (error) {
    // trackAnalytics already reports its own failures to Sentry.
  }
};

/**
 * A generated (not uploaded) document above version 1 is an edited document:
 * version 1 is the first draft, every later version comes from the user asking
 * for a change.
 */
export const isEditedGeneratedDocument = (docMessage) =>
  !!docMessage && !docMessage.is_user_uploaded && Number(docMessage.version) > 1;

// doc_id:version pairs already reported. The same version can arrive through both
// websocket paths or again on reconnect; it must count as one edit.
const reportedEdits = new Set();

export const resetReportedDocumentEdits = () => reportedEdits.clear();

/**
 * Fires "Document Edited By User" (INH-5930) when an edited version of a
 * generated document arrives. Ignores first drafts, uploaded documents and
 * versions already reported. Never throws (called from websocket handlers).
 */
export const trackDocumentEditedByUser = ({
  docMessage,
  threadId = null,
  metadata = null,
  editType = DOCUMENT_EDIT_TYPES.TEXT,
}) => {
  if (!isEditedGeneratedDocument(docMessage)) return;
  const key = `${docMessage.doc_id}:${docMessage.version}`;
  if (reportedEdits.has(key)) return;
  reportedEdits.add(key);
  try {
    trackAnalytics(
      EVENT_NAMES.DOCUMENT_EDITED_BY_USER,
      {
        [EVENT_PARAMETERS.EDIT_TYPE]: editType,
        [EVENT_PARAMETERS.DOCUMENT_VERSION]: Number(docMessage.version),
        [EVENT_PARAMETERS.PLAN_TIER]: metadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.THREAD_ID]: threadId,
        [EVENT_PARAMETERS.DOCUMENT_ID]: docMessage.doc_id ?? null,
        [EVENT_PARAMETERS.USER_ID]: metadata?.id ?? null,
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      },
      metadata,
    );
  } catch (error) {
    // trackAnalytics already reports its own failures to Sentry.
  }
};
